// ── Achievement System ──
// Succès visibles + secrets (VISIBLE_COUNT / TOTAL_COUNT dérivés de ALL_ACHIEVEMENTS)
// Checked client-side against player stats, collection, and progression

export interface AchievementProgress {
  current: number
  target: number
}

export interface AchievementDef {
  id: string
  nameFr: string
  nameEn: string
  descFr: string
  descEn: string
  icon: string
  hidden: boolean
  category: 'progression' | 'collection' | 'combat' | 'economy' | 'daycare' | 'secret'
  check: (ctx: AchievementContext) => boolean
  progress?: (ctx: AchievementContext) => AchievementProgress
}

export interface AchievementContext {
  level: number
  badges: number
  currentGeneration: number
  gold: number
  stats: Record<string, number>
  collectionCount: number
  shinyCount: number
  legendaryCount: number
  shinyLegendaryCount: number
  shinyLegendary5StarCount: number
  teamShinies: number
  teamLegendaries: number
  team5Stars: number
  teamShinyLegendary5Stars: number
  teamAllZeroDps: boolean
  showcase: Array<{ slug: string; isShiny: boolean }>
  devLinkClicked: boolean
  completedPokedexGens: number[]
  /** Nombre de succès visibles (non cachés) déjà débloqués — pour Complétionniste, pas le total brut. */
  unlockedVisibleCount: number
  /** 6 Pokémon en équipe : aucun type en double (chaque type n’apparaît qu’une fois au total). */
  teamTypesNoDuplicate: boolean
  /** 6 starters feu de base différents (formes de base uniquement). */
  teamAllBaseFireStarters: boolean
  /** 6 starters plante de base différents. */
  teamAllBaseGrassStarters: boolean
  /** 6 starters eau de base différents. */
  teamAllBaseWaterStarters: boolean
  /** Équipe = première équipe de Sacha (6 Pokémon). */
  teamAshFirst: boolean
  /** Arbok, Qulbutoké, Miaouss de Kanto, Smogogo en équipe. */
  teamRocketArsenal: boolean
  /** Keunotor chromatique niveau 100 dans la collection. */
  ownsShinyBidoofLevel100: boolean
  /** Toutes les espèces du Pokédex principal possédées en version chromatique. */
  ownsFullShinyPokedex: boolean
  /** 6 Pokémon en équipe partageant au moins un type commun à tous. */
  teamSharedCommonType: boolean
  /** 6 Pokémon en équipe, tous en forme de base (non évolués). */
  teamAllBaseForms: boolean
  /** 6 Pokémon niveau 100 restés en forme de base alors qu'ils peuvent évoluer. */
  teamEvolutionRefused: boolean
  /** 6 Pokémon en équipe, tous de type Spectre. */
  teamAllGhost: boolean
  /** 6 Pokémon en équipe, tous de type Dragon. */
  teamAllDragon: boolean
  /** Florizarre, Dracaufeu et Tortank présents en équipe. */
  teamKantoFinalStarters: boolean
  /** 6 évolutions distinctes d'Évoli en équipe. */
  teamSixEeveelutions: boolean
  /** Artikodin, Électhor et Sulfura en équipe. */
  teamKantoBirds: boolean
  /** Raikou, Entei et Suicune en équipe. */
  teamJohtoBeasts: boolean
  /** Groudon, Kyogre et Rayquaza en équipe. */
  teamWeatherTrio: boolean
  /** Dialga, Palkia et Giratina en équipe. */
  teamCreationTrio: boolean
  /** Reshiram, Zekrom et Kyurem en équipe. */
  teamTaoTrio: boolean
  /** Latios et Latias en équipe. */
  teamEonDuo: boolean
  /** Une méga-évolution présente en équipe. */
  teamHasMega: boolean
  /** Une forme régionale (Alola/Galar/Hisui/Paldea) présente en équipe. */
  teamHasRegionalForm: boolean
  /** Un légendaire chromatique niveau 100 dans la collection. */
  ownsShinyLegendaryLevel100: boolean
  /** Malvalame, Amphinobi, Mentali et Moumouflon réunis en équipe. */
  teamFourArchitects: boolean
}

function stat(ctx: AchievementContext, key: string): number {
  const v = ctx.stats[key]
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isFinite(n) ? n : 0
  }
  return 0
}

function pField(field: keyof AchievementContext, target: number): (ctx: AchievementContext) => AchievementProgress {
  return (ctx) => ({ current: Math.min(ctx[field] as number, target), target })
}

function pStat(key: string, target: number): (ctx: AchievementContext) => AchievementProgress {
  return (ctx) => ({ current: Math.min(stat(ctx, key), target), target })
}

// ── Visible achievements ──

const PROGRESSION: AchievementDef[] = [
  {
    id: 'first_badge', nameFr: 'Premier Pas', nameEn: 'First Step',
    descFr: 'Obtenir ton premier badge', descEn: 'Earn your first badge',
    icon: '🏅', hidden: false, category: 'progression',
    check: (ctx) => ctx.badges >= 1, progress: pField('badges', 1),
  },
  {
    id: 'badges_13', nameFr: 'Maître de Kanto', nameEn: 'Kanto Master',
    descFr: 'Obtenir les 13 badges de Kanto', descEn: 'Earn all 13 Kanto badges',
    icon: '🏆', hidden: false, category: 'progression',
    check: (ctx) => ctx.badges >= 13, progress: pField('badges', 13),
  },
  {
    id: 'badges_26', nameFr: 'Double Champion', nameEn: 'Double Champion',
    descFr: 'Obtenir 26 badges', descEn: 'Earn 26 badges',
    icon: '🏆', hidden: false, category: 'progression',
    check: (ctx) => ctx.badges >= 26, progress: pField('badges', 26),
  },
  {
    id: 'badges_52', nameFr: 'Quadruple Champion', nameEn: 'Quad Champion',
    descFr: 'Obtenir 52 badges', descEn: 'Earn 52 badges',
    icon: '🏆', hidden: false, category: 'progression',
    check: (ctx) => ctx.badges >= 52, progress: pField('badges', 52),
  },
  {
    id: 'badges_78', nameFr: 'Légende Vivante', nameEn: 'Living Legend',
    descFr: 'Obtenir 78 badges', descEn: 'Earn 78 badges',
    icon: '👑', hidden: false, category: 'progression',
    check: (ctx) => ctx.badges >= 78, progress: pField('badges', 78),
  },
  {
    id: 'badges_117', nameFr: 'Champion Suprême', nameEn: 'Supreme Champion',
    descFr: 'Obtenir tous les 117 badges', descEn: 'Earn all 117 badges',
    icon: '👑', hidden: false, category: 'progression',
    check: (ctx) => ctx.badges >= 117, progress: pField('badges', 117),
  },
  {
    id: 'level_10', nameFr: 'Dresseur Novice', nameEn: 'Novice Trainer',
    descFr: 'Atteindre le niveau 10', descEn: 'Reach level 10',
    icon: '⚡', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 10, progress: pField('level', 10),
  },
  {
    id: 'level_25', nameFr: 'Dresseur Confirmé', nameEn: 'Skilled Trainer',
    descFr: 'Atteindre le niveau 25', descEn: 'Reach level 25',
    icon: '⚡', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 25, progress: pField('level', 25),
  },
  {
    id: 'level_50', nameFr: 'Dresseur Expert', nameEn: 'Expert Trainer',
    descFr: 'Atteindre le niveau 50', descEn: 'Reach level 50',
    icon: '⚡', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 50, progress: pField('level', 50),
  },
  {
    id: 'level_100', nameFr: 'Dresseur Vétéran', nameEn: 'Veteran Trainer',
    descFr: 'Atteindre le niveau 100', descEn: 'Reach level 100',
    icon: '⚡', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 100, progress: pField('level', 100),
  },
  {
    id: 'level_200', nameFr: 'Maître Dresseur', nameEn: 'Master Trainer',
    descFr: 'Atteindre le niveau 200', descEn: 'Reach level 200',
    icon: '⭐', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 200, progress: pField('level', 200),
  },
  {
    id: 'level_350', nameFr: 'Élite des Dresseurs', nameEn: 'Trainer Elite',
    descFr: 'Atteindre le niveau 350', descEn: 'Reach level 350',
    icon: '⭐', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 350, progress: pField('level', 350),
  },
  {
    id: 'level_500', nameFr: 'Dresseur Ultime', nameEn: 'Ultimate Trainer',
    descFr: 'Atteindre le niveau 500', descEn: 'Reach level 500',
    icon: '👑', hidden: false, category: 'progression',
    check: (ctx) => ctx.level >= 500, progress: pField('level', 500),
  },
]

const REGIONS: AchievementDef[] = [
  {
    id: 'gen_2', nameFr: 'Bienvenue à Johto', nameEn: 'Welcome to Johto',
    descFr: 'Débloquer la région de Johto', descEn: 'Unlock the Johto region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 2, progress: pField('badges', 13),
  },
  {
    id: 'gen_3', nameFr: 'Cap sur Hoenn', nameEn: 'Onward to Hoenn',
    descFr: 'Débloquer la région de Hoenn', descEn: 'Unlock the Hoenn region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 3, progress: pField('badges', 26),
  },
  {
    id: 'gen_4', nameFr: 'Direction Sinnoh', nameEn: 'Heading to Sinnoh',
    descFr: 'Débloquer la région de Sinnoh', descEn: 'Unlock the Sinnoh region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 4, progress: pField('badges', 39),
  },
  {
    id: 'gen_5', nameFr: 'Aventure à Unys', nameEn: 'Adventure in Unova',
    descFr: 'Débloquer la région d\'Unys', descEn: 'Unlock the Unova region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 5, progress: pField('badges', 52),
  },
  {
    id: 'gen_6', nameFr: 'Escale à Kalos', nameEn: 'Stopover in Kalos',
    descFr: 'Débloquer la région de Kalos', descEn: 'Unlock the Kalos region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 6, progress: pField('badges', 65),
  },
  {
    id: 'gen_7', nameFr: 'Aloha, Alola !', nameEn: 'Aloha, Alola!',
    descFr: 'Débloquer la région d\'Alola', descEn: 'Unlock the Alola region',
    icon: '🌴', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 7, progress: pField('badges', 78),
  },
  {
    id: 'gen_8', nameFr: 'Tea Time à Galar', nameEn: 'Tea Time in Galar',
    descFr: 'Débloquer la région de Galar', descEn: 'Unlock the Galar region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 8, progress: pField('badges', 91),
  },
  {
    id: 'gen_9', nameFr: 'Vamos a Paldea', nameEn: 'Vamos to Paldea',
    descFr: 'Débloquer la région de Paldea', descEn: 'Unlock the Paldea region',
    icon: '🗺️', hidden: false, category: 'progression',
    check: (ctx) => ctx.currentGeneration >= 9, progress: pField('badges', 104),
  },
]

const COLLECTION: AchievementDef[] = [
  {
    id: 'pokemon_10', nameFr: 'Collectionneur Débutant', nameEn: 'Beginner Collector',
    descFr: 'Posséder 10 Pokémon', descEn: 'Own 10 Pokémon',
    icon: '📦', hidden: false, category: 'collection',
    check: (ctx) => ctx.collectionCount >= 10, progress: pField('collectionCount', 10),
  },
  {
    id: 'pokemon_50', nameFr: 'Collectionneur Amateur', nameEn: 'Amateur Collector',
    descFr: 'Posséder 50 Pokémon', descEn: 'Own 50 Pokémon',
    icon: '📦', hidden: false, category: 'collection',
    check: (ctx) => ctx.collectionCount >= 50, progress: pField('collectionCount', 50),
  },
  {
    id: 'pokemon_100', nameFr: 'Collectionneur Averti', nameEn: 'Skilled Collector',
    descFr: 'Posséder 100 Pokémon', descEn: 'Own 100 Pokémon',
    icon: '📦', hidden: false, category: 'collection',
    check: (ctx) => ctx.collectionCount >= 100, progress: pField('collectionCount', 100),
  },
  {
    id: 'pokemon_250', nameFr: 'Grand Collectionneur', nameEn: 'Great Collector',
    descFr: 'Posséder 250 Pokémon', descEn: 'Own 250 Pokémon',
    icon: '📦', hidden: false, category: 'collection',
    check: (ctx) => ctx.collectionCount >= 250, progress: pField('collectionCount', 250),
  },
  {
    id: 'pokemon_500', nameFr: 'Collectionneur Légendaire', nameEn: 'Legendary Collector',
    descFr: 'Posséder 500 Pokémon', descEn: 'Own 500 Pokémon',
    icon: '🏛️', hidden: false, category: 'collection',
    check: (ctx) => ctx.collectionCount >= 500, progress: pField('collectionCount', 500),
  },
  {
    id: 'first_shiny', nameFr: 'Première Étincelle', nameEn: 'First Sparkle',
    descFr: 'Obtenir ton premier shiny', descEn: 'Get your first shiny',
    icon: '✨', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyCount >= 1, progress: pField('shinyCount', 1),
  },
  {
    id: 'shiny_10', nameFr: 'Chasseur de Shiny', nameEn: 'Shiny Hunter',
    descFr: 'Posséder 10 shinies', descEn: 'Own 10 shinies',
    icon: '✨', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyCount >= 10, progress: pField('shinyCount', 10),
  },
  {
    id: 'shiny_25', nameFr: 'Chasseur Confirmé', nameEn: 'Skilled Hunter',
    descFr: 'Posséder 25 shinies', descEn: 'Own 25 shinies',
    icon: '✨', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyCount >= 25, progress: pField('shinyCount', 25),
  },
  {
    id: 'shiny_50', nameFr: 'Chasseur Expert', nameEn: 'Expert Hunter',
    descFr: 'Posséder 50 shinies', descEn: 'Own 50 shinies',
    icon: '✨', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyCount >= 50, progress: pField('shinyCount', 50),
  },
  {
    id: 'shiny_100', nameFr: 'Maître des Shinies', nameEn: 'Shiny Master',
    descFr: 'Posséder 100 shinies', descEn: 'Own 100 shinies',
    icon: '💫', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyCount >= 100, progress: pField('shinyCount', 100),
  },
  {
    id: 'first_legendary', nameFr: 'Contact Divin', nameEn: 'Divine Contact',
    descFr: 'Obtenir ton premier légendaire', descEn: 'Get your first legendary',
    icon: '🔱', hidden: false, category: 'collection',
    check: (ctx) => ctx.legendaryCount >= 1, progress: pField('legendaryCount', 1),
  },
  {
    id: 'legendary_10', nameFr: 'Panthéon', nameEn: 'Pantheon',
    descFr: 'Posséder 10 légendaires', descEn: 'Own 10 legendaries',
    icon: '🔱', hidden: false, category: 'collection',
    check: (ctx) => ctx.legendaryCount >= 10, progress: pField('legendaryCount', 10),
  },
  {
    id: 'legendary_25', nameFr: 'Demi-Dieu', nameEn: 'Demigod',
    descFr: 'Posséder 25 légendaires', descEn: 'Own 25 legendaries',
    icon: '🔱', hidden: false, category: 'collection',
    check: (ctx) => ctx.legendaryCount >= 25, progress: pField('legendaryCount', 25),
  },
  {
    id: 'shiny_legendary_1', nameFr: 'Mythe Incarné', nameEn: 'Living Myth',
    descFr: 'Obtenir un légendaire shiny', descEn: 'Get a shiny legendary',
    icon: '🌟', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyLegendaryCount >= 1, progress: pField('shinyLegendaryCount', 1),
  },
  {
    id: 'shiny_leg_5star_1', nameFr: 'Perfection Divine', nameEn: 'Divine Perfection',
    descFr: 'Obtenir un légendaire shiny 5★', descEn: 'Get a 5★ shiny legendary',
    icon: '👑', hidden: false, category: 'collection',
    check: (ctx) => ctx.shinyLegendary5StarCount >= 1, progress: pField('shinyLegendary5StarCount', 1),
  },
]

const COMBAT: AchievementDef[] = [
  {
    id: 'damage_1m', nameFr: 'Frappe Puissante', nameEn: 'Power Strike',
    descFr: 'Infliger 1M de dégâts au total', descEn: 'Deal 1M total damage',
    icon: '⚔️', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'totalDamage') >= 1_000_000, progress: pStat('totalDamage', 1_000_000),
  },
  {
    id: 'damage_100m', nameFr: 'Force Brute', nameEn: 'Brute Force',
    descFr: 'Infliger 100M de dégâts au total', descEn: 'Deal 100M total damage',
    icon: '⚔️', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'totalDamage') >= 100_000_000, progress: pStat('totalDamage', 100_000_000),
  },
  {
    id: 'kills_100', nameFr: 'Chasseur', nameEn: 'Hunter',
    descFr: 'Vaincre 100 ennemis', descEn: 'Defeat 100 enemies',
    icon: '🎯', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'totalKills') >= 100, progress: pStat('totalKills', 100),
  },
  {
    id: 'kills_1k', nameFr: 'Exterminateur', nameEn: 'Exterminator',
    descFr: 'Vaincre 1 000 ennemis', descEn: 'Defeat 1,000 enemies',
    icon: '🎯', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'totalKills') >= 1_000, progress: pStat('totalKills', 1_000),
  },
  {
    id: 'kills_10k', nameFr: 'Machine de Guerre', nameEn: 'War Machine',
    descFr: 'Vaincre 10 000 ennemis', descEn: 'Defeat 10,000 enemies',
    icon: '🎯', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'totalKills') >= 10_000, progress: pStat('totalKills', 10_000),
  },
  {
    id: 'kills_100k', nameFr: 'Apocalypse', nameEn: 'Apocalypse',
    descFr: 'Vaincre 100 000 ennemis', descEn: 'Defeat 100,000 enemies',
    icon: '💀', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'totalKills') >= 100_000, progress: pStat('totalKills', 100_000),
  },
  {
    id: 'desc_first_clear', nameFr: 'Première strate', nameEn: 'First strata',
    descFr: 'Terminer une Descente du sanctuaire', descEn: 'Clear a Descent of the Sanctuary run',
    icon: '🏔️', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'descRunsCleared') >= 1, progress: pStat('descRunsCleared', 1),
  },
  {
    id: 'desc_five_clears', nameFr: 'Pèlerin des profondeurs', nameEn: 'Depth pilgrim',
    descFr: 'Terminer 5 Descentes du sanctuaire', descEn: 'Clear 5 Descent runs',
    icon: '⛰️', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'descRunsCleared') >= 5, progress: pStat('descRunsCleared', 5),
  },
  {
    id: 'desc_mythic_clear', nameFr: 'Sanctuaire dompté', nameEn: 'Sanctuary tamed',
    descFr: 'Terminer une descente en Ascension II', descEn: 'Clear a descent on Ascension II',
    icon: '🔮', hidden: false, category: 'combat',
    check: (ctx) => stat(ctx, 'descClearAsc2') >= 1, progress: pStat('descClearAsc2', 1),
  },
]

const ECONOMY: AchievementDef[] = [
  {
    id: 'gold_100k', nameFr: 'Petit Bourgeois', nameEn: 'Petty Bourgeois',
    descFr: 'Dépenser 100 000 or', descEn: 'Spend 100,000 gold',
    icon: '💰', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'goldSpent') >= 100_000, progress: pStat('goldSpent', 100_000),
  },
  {
    id: 'gold_10m', nameFr: 'Magnat', nameEn: 'Tycoon',
    descFr: 'Dépenser 10M d\'or', descEn: 'Spend 10M gold',
    icon: '💰', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'goldSpent') >= 10_000_000, progress: pStat('goldSpent', 10_000_000),
  },
  {
    id: 'gold_100m', nameFr: 'Crésus', nameEn: 'Croesus',
    descFr: 'Dépenser 100M d\'or', descEn: 'Spend 100M gold',
    icon: '💰', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'goldSpent') >= 100_000_000, progress: pStat('goldSpent', 100_000_000),
  },
  {
    id: 'gold_1b', nameFr: 'Milliardaire', nameEn: 'Billionaire',
    descFr: 'Dépenser 1B d\'or', descEn: 'Spend 1B gold',
    icon: '🌟', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'goldSpent') >= 1_000_000_000, progress: pStat('goldSpent', 1_000_000_000),
  },
  {
    id: 'summons_100', nameFr: 'Invocateur', nameEn: 'Summoner',
    descFr: 'Effectuer 100 invocations', descEn: 'Perform 100 summons',
    icon: '🎰', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'totalSummons') >= 100, progress: pStat('totalSummons', 100),
  },
  {
    id: 'summons_500', nameFr: 'Invocateur Assidu', nameEn: 'Dedicated Summoner',
    descFr: 'Effectuer 500 invocations', descEn: 'Perform 500 summons',
    icon: '🎰', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'totalSummons') >= 500, progress: pStat('totalSummons', 500),
  },
  {
    id: 'summons_1000', nameFr: 'Maître Invocateur', nameEn: 'Master Summoner',
    descFr: 'Effectuer 1 000 invocations', descEn: 'Perform 1,000 summons',
    icon: '🎰', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'totalSummons') >= 1_000, progress: pStat('totalSummons', 1_000),
  },
  {
    id: 'summons_5000', nameFr: 'Invocateur Suprême', nameEn: 'Supreme Summoner',
    descFr: 'Effectuer 5 000 invocations', descEn: 'Perform 5,000 summons',
    icon: '🎲', hidden: false, category: 'economy',
    check: (ctx) => stat(ctx, 'totalSummons') >= 5_000, progress: pStat('totalSummons', 5_000),
  },
]

const DAYCARE: AchievementDef[] = [
  {
    id: 'daycare_1', nameFr: 'Premier Œuf', nameEn: 'First Egg',
    descFr: 'Faire éclore 1 œuf', descEn: 'Hatch 1 egg',
    icon: '🥚', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 1, progress: pStat('daycareHatches', 1),
  },
  {
    id: 'daycare_10', nameFr: 'Éleveur Amateur', nameEn: 'Amateur Breeder',
    descFr: 'Faire éclore 10 œufs', descEn: 'Hatch 10 eggs',
    icon: '🥚', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 10, progress: pStat('daycareHatches', 10),
  },
  {
    id: 'daycare_50', nameFr: 'Éleveur Pro', nameEn: 'Pro Breeder',
    descFr: 'Faire éclore 50 œufs', descEn: 'Hatch 50 eggs',
    icon: '🐣', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 50, progress: pStat('daycareHatches', 50),
  },
  {
    id: 'daycare_500', nameFr: 'Éleveur Expert', nameEn: 'Expert Breeder',
    descFr: 'Faire éclore 500 œufs', descEn: 'Hatch 500 eggs',
    icon: '🐣', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 500, progress: pStat('daycareHatches', 500),
  },
  {
    id: 'daycare_2000', nameFr: 'Éleveur Légendaire', nameEn: 'Legendary Breeder',
    descFr: 'Faire éclore 2 000 œufs', descEn: 'Hatch 2,000 eggs',
    icon: '🐣', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 2_000, progress: pStat('daycareHatches', 2_000),
  },
  {
    id: 'daycare_5000', nameFr: 'Maître Éleveur', nameEn: 'Master Breeder',
    descFr: 'Faire éclore 5 000 œufs', descEn: 'Hatch 5,000 eggs',
    icon: '🏭', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 5_000, progress: pStat('daycareHatches', 5_000),
  },
  {
    id: 'daycare_10000', nameFr: 'Usine à Œufs', nameEn: 'Egg Factory',
    descFr: 'Faire éclore 10 000 œufs', descEn: 'Hatch 10,000 eggs',
    icon: '🏭', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 10_000, progress: pStat('daycareHatches', 10_000),
  },
  {
    id: 'candies_50', nameFr: 'Gourmand', nameEn: 'Sweet Tooth',
    descFr: 'Utiliser 50 bonbons', descEn: 'Use 50 candies',
    icon: '🍬', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'candiesUsed') >= 50, progress: pStat('candiesUsed', 50),
  },
  {
    id: 'candies_200', nameFr: 'Confiseur', nameEn: 'Confectioner',
    descFr: 'Utiliser 200 bonbons', descEn: 'Use 200 candies',
    icon: '🍬', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'candiesUsed') >= 200, progress: pStat('candiesUsed', 200),
  },
  {
    id: 'candies_1000', nameFr: 'Baron du Bonbon', nameEn: 'Candy Baron',
    descFr: 'Utiliser 1 000 bonbons', descEn: 'Use 1,000 candies',
    icon: '🍭', hidden: false, category: 'daycare',
    check: (ctx) => stat(ctx, 'candiesUsed') >= 1_000, progress: pStat('candiesUsed', 1_000),
  },
]

// ── Hidden achievements ──

const SECRET: AchievementDef[] = [
  {
    id: 'secret_all_regions', nameFr: 'Globe-Trotter', nameEn: 'Globe-Trotter',
    descFr: 'Débloquer toutes les régions', descEn: 'Unlock all regions',
    icon: '🌍', hidden: true, category: 'secret',
    check: (ctx) => ctx.currentGeneration >= 9,
  },
  {
    id: 'secret_shiny_team', nameFr: 'Constellation Brillante', nameEn: 'Shining Constellation',
    descFr: 'Aligner une équipe de 6 Pokémon chromatiques', descEn: 'Field a team of 6 shiny Pokémon',
    icon: '✨', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamShinies >= 6, progress: pField('teamShinies', 6),
  },
  {
    id: 'secret_legendary_team', nameFr: 'Assemblée Divine', nameEn: 'Divine Assembly',
    descFr: 'Aligner une équipe de 6 légendaires', descEn: 'Field a team of 6 legendaries',
    icon: '🔱', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamLegendaries >= 6, progress: pField('teamLegendaries', 6),
  },
  {
    id: 'secret_5star_team', nameFr: 'Apothéose', nameEn: 'Apotheosis',
    descFr: 'Aligner une équipe de 6 Pokémon 5★ (Divin)', descEn: 'Field a team of 6 Pokémon at 5★ (Divine)',
    icon: '⭐', hidden: true, category: 'secret',
    check: (ctx) => ctx.team5Stars >= 6, progress: pField('team5Stars', 6),
  },
  {
    id: 'secret_millionaire', nameFr: 'Milliardaire Secret', nameEn: 'Secret Billionaire',
    descFr: 'Posséder 1B or en même temps', descEn: 'Have 1B gold at once',
    icon: '💰', hidden: true, category: 'secret',
    check: (ctx) => ctx.gold >= 1_000_000_000,
  },
  {
    id: 'secret_playtime_24h', nameFr: 'Noctambule', nameEn: 'Night Owl',
    descFr: 'Jouer pendant 24h au total', descEn: 'Play for 24 hours total',
    icon: '🌙', hidden: true, category: 'secret',
    check: (ctx) => stat(ctx, 'playTimeSeconds') >= 86_400, progress: pStat('playTimeSeconds', 86_400),
  },
  {
    id: 'secret_pokedex_kanto', nameFr: 'Professeur Chen', nameEn: 'Professor Oak',
    descFr: 'Compléter le Pokédex de Kanto', descEn: 'Complete the Kanto Pokédex',
    icon: '📖', hidden: true, category: 'secret',
    check: (ctx) => ctx.completedPokedexGens.includes(1),
  },
  {
    id: 'secret_pokedex_all', nameFr: 'Encyclopédie Vivante', nameEn: 'Living Encyclopedia',
    descFr: 'Compléter le Pokédex de toutes les régions', descEn: 'Complete the Pokédex of all regions',
    icon: '📚', hidden: true, category: 'secret',
    check: (ctx) => [1, 2, 3, 4, 5, 6, 7, 8, 9].every((g) => ctx.completedPokedexGens.includes(g)),
  },
  {
    id: 'secret_shiny_pokedex_all', nameFr: 'Encyclopédie Chromatique', nameEn: 'Chromatic Encyclopedia',
    descFr: 'Posséder toutes les espèces du Pokédex en version chromatique', descEn: 'Own every Pokédex species in shiny form',
    icon: '🌈', hidden: true, category: 'secret',
    check: (ctx) => ctx.ownsFullShinyPokedex,
  },
  {
    id: 'secret_damage_1b', nameFr: 'Annihilation', nameEn: 'Annihilation',
    descFr: 'Infliger 1 milliard de dégâts au total (monde)', descEn: 'Deal 1 billion total damage (world)',
    icon: '☄️', hidden: true, category: 'secret',
    check: (ctx) => stat(ctx, 'totalDamage') >= 1_000_000_000, progress: pStat('totalDamage', 1_000_000_000),
  },
  {
    id: 'secret_summons_50000', nameFr: 'Gacha Addict', nameEn: 'Gacha Addict',
    descFr: 'Effectuer 50 000 invocations', descEn: 'Perform 50,000 summons',
    icon: '🎲', hidden: true, category: 'secret',
    check: (ctx) => stat(ctx, 'totalSummons') >= 50_000, progress: pStat('totalSummons', 50_000),
  },
  {
    id: 'secret_shiny_leg_10', nameFr: 'Olympe', nameEn: 'Mount Olympus',
    descFr: 'Posséder 10 légendaires shiny', descEn: 'Own 10 shiny legendaries',
    icon: '⚡', hidden: true, category: 'secret',
    check: (ctx) => ctx.shinyLegendaryCount >= 10, progress: pField('shinyLegendaryCount', 10),
  },
  {
    id: 'secret_daycare_50000', nameFr: 'Nurserie Industrielle', nameEn: 'Industrial Nursery',
    descFr: 'Faire éclore 30 000 œufs', descEn: 'Hatch 30,000 eggs',
    icon: '🏭', hidden: true, category: 'secret',
    check: (ctx) => stat(ctx, 'daycareHatches') >= 30_000, progress: pStat('daycareHatches', 30_000),
  },
  {
    id: 'secret_all_visible', nameFr: 'Complétionniste', nameEn: 'Completionist',
    descFr: 'Débloquer tous les succès visibles', descEn: 'Unlock all visible achievements',
    icon: '🏆', hidden: true, category: 'secret',
    check: (ctx) => ctx.unlockedVisibleCount >= VISIBLE_COUNT,
    progress: (ctx) => ({ current: Math.min(ctx.unlockedVisibleCount, VISIBLE_COUNT), target: VISIBLE_COUNT }),
  },
  {
    id: 'reserve_red_defeated', nameFr: 'Vainqueur de Red', nameEn: 'Red Vanquisher',
    descFr: 'Vaincre Red (épreuve finale de la réserve, zone 19)', descEn: 'Defeat Red (reserve final trial, zone 19)',
    icon: '🔴', hidden: true, category: 'secret',
    check: (ctx) => stat(ctx, '_btCapstoneDefeated') >= 1,
  },
  {
    id: 'secret_dev_link', nameFr: 'Fan du Dev', nameEn: 'Dev Fan',
    descFr: 'Cliquer sur le lien de Jérémy Duc dans le guide', descEn: 'Click the Jérémy Duc link in the guide',
    icon: '💻', hidden: true, category: 'secret',
    check: (ctx) => ctx.devLinkClicked,
  },
  {
    id: 'secret_malvalame', nameFr: 'Le Choix de Nashoba', nameEn: "Nashoba's Pick",
    descFr: 'Mettre Malvalame en favori', descEn: 'Put Ceruledge in your favorites',
    icon: '🔥', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'ceruledge'),
  },
  {
    id: 'secret_showcase_pikachu', nameFr: 'Tu Connais', nameEn: 'You Know the One',
    descFr: 'Mettre Pikachu en favori', descEn: 'Put Pikachu in your favorites',
    icon: '⚡', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'pikachu'),
  },
  {
    id: 'secret_team_types_unique', nameFr: 'Arc-en-ciel', nameEn: 'Rainbow',
    descFr: 'Aligner une équipe de 6 sans aucun type en double', descEn: 'Field a team of 6 with no duplicate type',
    icon: '🎨', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamTypesNoDuplicate,
  },
  {
    id: 'secret_team_fire_starters', nameFr: 'Brasier Originel', nameEn: 'Primal Blaze',
    descFr: 'Aligner 6 starters Feu de base différents', descEn: 'Field 6 different base Fire starters',
    icon: '🔥', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllBaseFireStarters,
  },
  {
    id: 'secret_team_grass_starters', nameFr: 'Sylve Primordiale', nameEn: 'Primeval Grove',
    descFr: 'Aligner 6 starters Plante de base différents', descEn: 'Field 6 different base Grass starters',
    icon: '🌿', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllBaseGrassStarters,
  },
  {
    id: 'secret_team_water_starters', nameFr: 'Déferlante', nameEn: 'Tidal Surge',
    descFr: 'Aligner 6 starters Eau de base différents', descEn: 'Field 6 different base Water starters',
    icon: '💧', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllBaseWaterStarters,
  },
  {
    id: 'secret_showcase_meowth_kanto', nameFr: 'Préparez-vous à des Ennuis', nameEn: 'Prepare for Trouble',
    descFr: 'Mettre Miaouss de Kanto en favori', descEn: 'Put Kantonian Meowth in your favorites',
    icon: '🐱', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'meowth'),
  },
  {
    id: 'secret_shiny_bidoof_100', nameFr: 'Maître Keunotor', nameEn: 'Bidoof Master',
    descFr: 'Posséder un Keunotor chromatique au niveau 100', descEn: 'Own a level 100 shiny Bidoof',
    icon: '🦫', hidden: true, category: 'secret',
    check: (ctx) => ctx.ownsShinyBidoofLevel100,
  },
  {
    id: 'secret_team_ash', nameFr: 'Première Ligue', nameEn: 'First League',
    descFr: 'Équipe : Pikachu, Papilusion, Roucarnage, Bulbizarre, Dracaufeu et Carapuce', descEn: 'Team: Pikachu, Butterfree, Pidgeot, Bulbasaur, Charizard, and Squirtle',
    icon: '⭐', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAshFirst,
  },
  {
    id: 'secret_showcase_greninja_bad_taste', nameFr: "Le Choix d'Ashborn", nameEn: "Ashborn's Pick",
    descFr: 'Mettre Amphinobi en favori', descEn: 'Put Greninja in your favorites',
    icon: '🥷', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'greninja'),
  },
  {
    id: 'secret_team_rocket_arsenal', nameFr: "L'Arsenal Rocket", nameEn: 'Rocket Arsenal',
    descFr: 'Équipe avec Arbok, Qulbutoké, Miaouss de Kanto et Smogogo', descEn: 'Team with Arbok, Wobbuffet, Kantonian Meowth, and Weezing',
    icon: '💣', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamRocketArsenal,
  },
  {
    id: 'secret_showcase_fan_legendaries', nameFr: 'Je suis fan des pokemons', nameEn: 'I Love Pokémon',
    descFr: 'Mettre Kyurem, Méga-Rayquaza, Arceus, Xerneas, Groudon et Mewtwo en favoris', descEn: 'Favorite Kyurem, Mega Rayquaza, Arceus, Xerneas, Groudon, and Mewtwo',
    icon: '💖', hidden: true, category: 'secret',
    check: (ctx) =>
      ['kyurem', 'rayquaza-mega', 'arceus', 'xerneas', 'groudon', 'mewtwo'].every((slug) =>
        ctx.showcase.some((s) => s.slug === slug),
      ),
  },
  {
    id: 'secret_showcase_arceus', nameFr: 'Au Commencement', nameEn: 'In the Beginning',
    descFr: 'Mettre Arceus en favori', descEn: 'Put Arceus in your favorites',
    icon: '🌌', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'arceus'),
  },
  {
    id: 'secret_showcase_snorlax', nameFr: 'Route Bloquée', nameEn: 'Roadblock',
    descFr: 'Mettre Ronflex en favori', descEn: 'Put Snorlax in your favorites',
    icon: '🛌', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'snorlax'),
  },
  {
    id: 'secret_zero_dps', nameFr: 'Pacifiste', nameEn: 'Pacifist',
    descFr: 'Aligner une équipe de 6 Pokémon ayant tous 0 DPS', descEn: 'Field a team of 6 Pokémon all at 0 DPS',
    icon: '☮️', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllZeroDps,
  },
  {
    id: 'secret_shiny_leg_5star_team', nameFr: 'Perfection Absolue', nameEn: 'Absolute Perfection',
    descFr: 'Aligner une équipe de 6 légendaires chromatiques 5★', descEn: 'Field a team of 6 shiny 5★ legendaries',
    icon: '✨', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamShinyLegendary5Stars >= 6, progress: pField('teamShinyLegendary5Stars', 6),
  },
  // ── Équipes spéciales (types / formes / trios & duos légendaires) ──
  {
    id: 'secret_team_shared_type', nameFr: 'Pureté Élémentaire', nameEn: 'Elemental Purity',
    descFr: 'Aligner une équipe de 6 partageant un même type', descEn: 'Field a team of 6 sharing a common type',
    icon: '🧬', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamSharedCommonType,
  },
  {
    id: 'secret_team_base_forms', nameFr: 'Retour aux Sources', nameEn: 'Back to the Roots',
    descFr: 'Aligner une équipe de 6 Pokémon en forme de base (non évolués)', descEn: 'Field a team of 6 base-form (unevolved) Pokémon',
    icon: '🌱', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllBaseForms,
  },
  {
    id: 'secret_team_base_forms_100', nameFr: 'Évolution Refusée', nameEn: 'Evolution Denied',
    descFr: 'Aligner 6 Pokémon niveau 100 restés à leur forme de base alors qu\'ils peuvent évoluer', descEn: 'Field 6 level 100 Pokémon kept in their base form despite being able to evolve',
    icon: '🚫', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamEvolutionRefused,
  },
  {
    id: 'secret_team_all_ghost', nameFr: 'Conseil des Fantômes', nameEn: 'Ghost Council',
    descFr: 'Aligner une équipe de 6 Pokémon de type Spectre', descEn: 'Field a team of 6 Ghost-type Pokémon',
    icon: '👻', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllGhost,
  },
  {
    id: 'secret_team_all_dragon', nameFr: 'Conseil des Dragons', nameEn: 'Dragon Council',
    descFr: 'Aligner une équipe de 6 Pokémon de type Dragon', descEn: 'Field a team of 6 Dragon-type Pokémon',
    icon: '🐉', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamAllDragon,
  },
  {
    id: 'secret_team_kanto_final_starters', nameFr: 'Le Choix du Prof', nameEn: "The Professor's Choice",
    descFr: "Réunir Florizarre, Dracaufeu et Tortank dans l'équipe", descEn: 'Bring Venusaur, Charizard, and Blastoise together in your team',
    icon: '🎓', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamKantoFinalStarters,
  },
  {
    id: 'secret_team_eeveelutions', nameFr: 'La Famille Évoli', nameEn: 'The Eevee Family',
    descFr: "Aligner une équipe de 6 évolutions différentes d'Évoli", descEn: 'Field a team of 6 different Eevee evolutions',
    icon: '🦊', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamSixEeveelutions,
  },
  {
    id: 'secret_team_kanto_birds', nameFr: 'Les Gardiens Ailés', nameEn: 'The Winged Guardians',
    descFr: "Réunir Artikodin, Électhor et Sulfura dans l'équipe", descEn: 'Bring Articuno, Zapdos, and Moltres together in your team',
    icon: '🕊️', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamKantoBirds,
  },
  {
    id: 'secret_team_johto_beasts', nameFr: 'La Meute de la Tour', nameEn: 'The Tower Pack',
    descFr: "Réunir Raikou, Entei et Suicune dans l'équipe", descEn: 'Bring Raikou, Entei, and Suicune together in your team',
    icon: '🐕', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamJohtoBeasts,
  },
  {
    id: 'secret_team_weather_trio', nameFr: 'Le Super-Ancien', nameEn: 'The Super-Ancient',
    descFr: "Réunir Groudon, Kyogre et Rayquaza dans l'équipe", descEn: 'Bring Groudon, Kyogre, and Rayquaza together in your team',
    icon: '🌋', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamWeatherTrio,
  },
  {
    id: 'secret_team_creation_trio', nameFr: 'Maîtres du Temps', nameEn: 'Masters of Time',
    descFr: "Réunir Dialga, Palkia et Giratina dans l'équipe", descEn: 'Bring Dialga, Palkia, and Giratina together in your team',
    icon: '⏳', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamCreationTrio,
  },
  {
    id: 'secret_team_tao_trio', nameFr: 'Le Dragon Originel', nameEn: 'The Original Dragon',
    descFr: "Réunir Reshiram, Zekrom et Kyurem dans l'équipe", descEn: 'Bring Reshiram, Zekrom, and Kyurem together in your team',
    icon: '🐲', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamTaoTrio,
  },
  {
    id: 'secret_team_eon_duo', nameFr: 'Duo Éon', nameEn: 'Eon Duo',
    descFr: "Réunir Latios et Latias dans l'équipe", descEn: 'Bring Latios and Latias together in your team',
    icon: '💞', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamEonDuo,
  },
  {
    id: 'secret_team_mega', nameFr: 'Éveil de la Pierre', nameEn: 'Stone Awakening',
    descFr: "Avoir un Pokémon Méga-Évolué dans l'équipe", descEn: 'Have a Mega-Evolved Pokémon in your team',
    icon: '💎', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamHasMega,
  },
  {
    id: 'secret_team_regional_form', nameFr: "Venu d'Ailleurs", nameEn: 'From Afar',
    descFr: "Avoir une forme régionale (Alola/Galar) dans l'équipe", descEn: 'Have a regional form (Alola/Galar) in your team',
    icon: '🌏', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamHasRegionalForm,
  },
  // ── Collection ──
  {
    id: 'secret_shiny_legendary_100', nameFr: 'Idole Chromatique', nameEn: 'Chromatic Idol',
    descFr: 'Posséder un légendaire chromatique au niveau 100', descEn: 'Own a level 100 shiny legendary',
    icon: '🌟', hidden: true, category: 'secret',
    check: (ctx) => ctx.ownsShinyLegendaryLevel100,
  },
  // ── Favoris (lore & clins d'œil) ──
  {
    id: 'secret_showcase_giratina', nameFr: 'Le Renégat', nameEn: 'The Renegade',
    descFr: 'Mettre Giratina en favori', descEn: 'Put Giratina in your favorites',
    icon: '👤', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'giratina'),
  },
  {
    id: 'secret_showcase_mew', nameFr: 'Le Mirage', nameEn: 'The Mirage',
    descFr: 'Mettre Mew en favori', descEn: 'Put Mew in your favorites',
    icon: '🌸', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'mew'),
  },
  {
    id: 'secret_showcase_mewtwo', nameFr: 'Némésis', nameEn: 'Nemesis',
    descFr: 'Mettre Mewtwo en favori', descEn: 'Put Mewtwo in your favorites',
    icon: '🔬', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'mewtwo'),
  },
  {
    id: 'secret_showcase_magikarp', nameFr: 'Sois Fort, Magicarpe', nameEn: 'Splash On, Magikarp',
    descFr: 'Mettre Magicarpe en favori', descEn: 'Put Magikarp in your favorites',
    icon: '🐟', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'magikarp'),
  },
  {
    id: 'secret_showcase_psyduck', nameFr: 'Migraine Aquatique', nameEn: 'Aquatic Migraine',
    descFr: 'Mettre Psykokwak en favori', descEn: 'Put Psyduck in your favorites',
    icon: '🦆', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'psyduck'),
  },
  {
    id: 'secret_showcase_ditto', nameFr: 'Caméléon Parfait', nameEn: 'Perfect Chameleon',
    descFr: 'Mettre Métamorph en favori', descEn: 'Put Ditto in your favorites',
    icon: '🟣', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'ditto'),
  },
  {
    id: 'secret_showcase_espeon', nameFr: "Le Choix d'Achlar", nameEn: "Achlar's Pick",
    descFr: 'Mettre Mentali en favori', descEn: 'Put Espeon in your favorites',
    icon: '🔮', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'espeon'),
  },
  {
    id: 'secret_showcase_dubwool', nameFr: 'Le Choix de Guillian', nameEn: "Guillian's Pick",
    descFr: 'Mettre Moumouflon en favori', descEn: 'Put Dubwool in your favorites',
    icon: '🐑', hidden: true, category: 'secret',
    check: (ctx) => ctx.showcase.some((s) => s.slug === 'dubwool'),
  },
  {
    id: 'secret_four_architects', nameFr: 'Les Quatre Architectes', nameEn: 'The Four Architects',
    descFr: 'Aligner Malvalame, Amphinobi, Mentali et Moumouflon dans la même équipe', descEn: 'Field Ceruledge, Greninja, Espeon, and Dubwool together in your team',
    icon: '🏛️', hidden: true, category: 'secret',
    check: (ctx) => ctx.teamFourArchitects,
  },
]

export const ALL_ACHIEVEMENTS: AchievementDef[] = [
  ...PROGRESSION,
  ...REGIONS,
  ...COLLECTION,
  ...COMBAT,
  ...ECONOMY,
  ...DAYCARE,
  ...SECRET,
]

export const VISIBLE_COUNT = ALL_ACHIEVEMENTS.filter((a) => !a.hidden).length
export const TOTAL_COUNT = ALL_ACHIEVEMENTS.length

/**
 * Nombre de succès **visibles** débloqués (pour Complétionniste / barre de progression).
 * Utilise un Set implicite par id — pas de double compte si la liste contient des doublons.
 */
export function countUnlockedVisibleAchievements(ids: readonly string[] | undefined): number {
  if (!ids?.length) return 0
  const unlocked = new Set(ids)
  return ALL_ACHIEVEMENTS.filter((a) => !a.hidden && unlocked.has(a.id)).length
}

export const CATEGORY_LABELS: Record<string, { fr: string; en: string }> = {
  progression: { fr: 'Progression', en: 'Progression' },
  collection: { fr: 'Collection', en: 'Collection' },
  combat: { fr: 'Combat', en: 'Combat' },
  economy: { fr: 'Économie', en: 'Economy' },
  daycare: { fr: 'Élevage', en: 'Breeding' },
  secret: { fr: 'Secrets', en: 'Secrets' },
}

export function getAchievementById(id: string): AchievementDef | undefined {
  return ALL_ACHIEVEMENTS.find((a) => a.id === id)
}
